/**
 * ScriptMatcher — LLM-Based Script Matching + Parameter Extraction
 *
 * Uses ONE LLM call to:
 * 1. Pick the best matching script from the library (or "none")
 * 2. Extract parameter values from the user question
 *
 * Why LLM over embeddings:
 * - Embeddings capture topic similarity ("overstock" ≈ "inventory" ≈ "revenue")
 *   but can't distinguish structurally different questions about the same domain
 * - LLM understands that "overstock by warehouse" needs a different script than
 *   "revenue by warehouse" even though they're semantically close
 * - One call does both matching AND parameter extraction
 *
 * When script library grows past ~50, add an embedding pre-filter
 * (ChromaDB narrows to top 10 → LLM picks from those 10).
 */

import { LLM } from '../../llm';
import { promptLoader } from '../prompt-loader';
import { getCurrentDateTimeForPrompt } from '../../utils/datetime';
import type { ScriptRecipe, ScriptMatch, MatchTier } from './types';
import type { ScriptStore } from './script-store';
import { logger } from '../../utils/logger';

const MAX_FAILURE_RATE = 0.2;

export class ScriptMatcher {
	private store: ScriptStore;

	constructor(store: ScriptStore) {
		this.store = store;
	}

	/**
	 * Find the best matching script for a user question.
	 * Uses ONE LLM call that picks the script AND extracts parameters.
	 * Returns null if no script matches.
	 */
	async match(
		userPrompt: string,
		apiKey?: string,
		model?: string,
		signal?: AbortSignal,
	): Promise<ScriptMatch | null> {
		// FTS shortlist — Postgres ranks the healthy verified recipes by relevance
		// to the prompt and returns the top-K. The catalog handed to the LLM is now
		// O(K), not O(all scripts). Health-filtering happens in SQL; we keep a light
		// client-side guard as defense in depth.
		const recipes = await this.store.search(userPrompt);
		if (recipes.length === 0) return null;

		const healthyRecipes = recipes.filter(r => {
			const total = r.successCount + r.failureCount;
			return total < 5 || r.failureCount / total <= MAX_FAILURE_RATE;
		});

		if (healthyRecipes.length === 0) {
			logger.info(`[ScriptMatcher] No healthy candidates among ${recipes.length} FTS matches — skipping`);
			return null;
		}

		// Build the script catalog for the LLM
		const scriptCatalog = this.buildScriptCatalog(healthyRecipes);

		try {
			// Load prompts from file system → falls back to hardcoded in prompts.ts
			const prompts = await promptLoader.loadPrompts('script-match', {
				SCRIPT_CATALOG: scriptCatalog,
				USER_PROMPT: userPrompt,
				CURRENT_DATE: getCurrentDateTimeForPrompt(),
			});

			logger.info(`[ScriptMatcher] Matching against ${healthyRecipes.length} scripts`);

			const response = await LLM.stream<any>(
				{ sys: prompts.system, user: prompts.user },
				{
					model: model || undefined,
					maxTokens: 500,
					temperature: 0,
					apiKey,
					signal,
				},
				true, // Parse as JSON
			);

			// Resolve tier — accept the new `tier` field, fall back to legacy
			// `confidence`-based parsing if an older prompt happens to be loaded.
			const rawTier = (response?.tier || '').toString().toLowerCase();
			let tier: MatchTier;
			if (rawTier === 'high' || rawTier === 'near' || rawTier === 'none') {
				tier = rawTier;
			} else if (response?.scriptId && response.scriptId !== 'none') {
				// Legacy fallback: treat any matched script as 'high'
				tier = response.confidence === 'low' ? 'none' : 'high';
			} else {
				tier = 'none';
			}

			// Binary reuse policy: fork/near mode is retired — the runtime acts only on
			// 'high' (replay unchanged) or 'none' (regenerate fresh). Coerce any stray
			// 'near' to 'none' so a script-match prompt regression can't silently re-enable
			// the dead fork-and-adapt path (its main-agent prompt section is removed too).
			// This makes the tier === 'near' branch below unreachable by design.
			if (tier === 'near') {
				logger.info(`[ScriptMatcher] 'near' coerced to 'none' (binary reuse policy — fork mode retired)`);
				tier = 'none';
			}

			const reasoning = response?.reasoning || 'unknown';

			if (tier === 'none' || !response?.scriptId || response.scriptId === 'none') {
				logger.info(`[ScriptMatcher] No match. Reason: ${reasoning}`);
				return null;
			}

			const recipe = await this.store.get(response.scriptId);
			if (!recipe) {
				logger.warn(`[ScriptMatcher] LLM returned unknown script ID: "${response.scriptId}"`);
				return null;
			}

			// tier === 'high' (only 'high' can reach here — 'none'/'near' returned/coerced above)
			logger.info(`[ScriptMatcher] Matched "${recipe.name}" (tier: high). Reason: ${reasoning}`);

			return {
				recipe,
				tier: 'high',
				similarity: 0.95,
				confidence: 'high',
				extractedParams: response.params || {},
				reasoning,
			};

		} catch (error) {
			const msg = error instanceof Error ? error.message : String(error);
			logger.error(`[ScriptMatcher] LLM matching failed: ${msg}`);
			return null;
		}
	}

	/**
	 * Build the script catalog string for the LLM prompt.
	 * Each script gets: index, ID, name, description, and parameter definitions.
	 */
	private buildScriptCatalog(recipes: ScriptRecipe[]): string {
		return recipes.map((r, idx) => {
			const paramList = r.parameters.length > 0
				? r.parameters.map(p => {
					let desc = `${p.name} (${p.type}`;
					if (!p.required) desc += ', optional';
					if (p.default !== undefined) desc += `, default: ${JSON.stringify(p.default)}`;
					if (p.enumValues) desc += `, values: ${Object.keys(p.enumValues).join('/')}`;
					desc += `) — ${p.description}`;
					return `    - ${desc}`;
				}).join('\n')
				: '    (no parameters)';

			return `${idx + 1}. [${r.id}] "${r.name}"\n   ${r.intentDescription}\n   Parameters:\n${paramList}`;
		}).join('\n\n');
	}
}
